/* eslint-disable no-nested-ternary */
/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, IconButton, Link, Typography, useTheme } from '@mui/material';

import IconEdit from '@assets/icons/edit.svg?react';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { RootState } from '../../../redux/store';
import { StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { titleStyles } from './infoStyles';
import { CustomGridApi } from '../XTable/types';
import { displayValueByType, getModelInfo } from '@/utils/structureUtils';
import FieldSearch from '../FieldSearch';
import { findNormalizedMatchRange, normalizeSearch } from '@/utils/stringUtils';

type GroupedFields = StructureType & {
 Key: string;
 Translation: string;
 Group: string;
 Value: any;
 InnerValue: string;
};

const InfoList = ({
 data,
 apiRef,
 customFields,
}: {
 data: any;
 customFields?: string[] | null;
 apiRef?: MutableRefObject<CustomGridApi>;
}) => {
 const { pageName } = usePage();
 const th = useTheme();
 //  const { openDialog: openFieldsConfigDialog } = useFieldsConfig();
 const { t } = useTranslation();
 const [searchValue, setSearchValue] = useState('');

 const structure = useSelector((state: RootState) => {
  if (pageName) {
   const pageState = state[pageName as keyof RootState];
   if (pageState && typeof pageState === 'object' && 'structure' in pageState) {
    return pageState.structure;
   }
  }
  return null;
 });

 const handleEditClick = () => {
  if (apiRef && apiRef.current) {
   apiRef.current?.setModalState({
    id: data.Id,
    action: 'update',
    //  tab: 1,
   });
  }
 };

 const fieldsWithData = useMemo(() => {
  const fields = customFields || Object.keys(data || {});

  if (!structure || !Array.isArray(fields)) return [];
  return fields?.map((field) => {
   const val = field.split('.').reduce((acc, part) => acc?.[part], data);
   const str = findStructure(structure, field);

   //  console.log(field, val, nameVar);
   let value = val;
   let innerValue = null;
   if (str?.Type === 'model' && val) {
    const { id, name } = getModelInfo(str?.ForeignKeyDefaultFilter || '');
    innerValue = data?.[field]?.[id];
    value = val[name];
   }
   if (str?.Type === 'array') {
    value = t('user.sidebar.infoList.noArraySupport');
   }
   if (str?.Type === 'boolean') {
    value =
     typeof val === 'boolean' ? (val ? t('global.yes') : t('global.no')) : '-';
   }
   if (!val) {
    value = val;
   }

   return {
    ...str,
    Key: field,
    Translation: getTranslation(structure, field),
    Value: value,
    InnerValue: innerValue,
    Group: str?.Group,
   };
  });
 }, [data, structure]);

 const groupsMemo = useMemo(() => {
  const gr = fieldsWithData
   .filter((f) => f.Type !== 'array' && f.Priority)
   .reduce((acc, f) => {
    acc[f.Group] = acc[f.Group] || [];
    acc[f.Group].push(f);
    return acc;
   }, {} as Record<string, GroupedFields[]>);

  const sortedGroups = Object.keys(gr).sort((a, b) => {
   if (a === 'null') return 1;
   if (b === 'null') return -1;

   return a.localeCompare(b);
  });
  const sortedObj: Record<string, GroupedFields[]> = {};
  for (const key of sortedGroups) {
   sortedObj[key] = [...gr[key]].sort(
    (a, b) => (a.Priority ?? 0) - (b.Priority ?? 0),
   );
  }

  return sortedObj;
 }, [fieldsWithData]);

 const groupKeys = Object.keys(groupsMemo);
const normalizedSearch = normalizeSearch(searchValue.trim());

 const highlightMatch = (text: string, query: string) => {
 const matchRange = findNormalizedMatchRange(text || '', query || '');
 if (!matchRange) return text;
 const start = text.slice(0, matchRange.start);
 const match = text.slice(matchRange.start, matchRange.end);
 const end = text.slice(matchRange.end);
  return (
   <>
    {start}
    <Box
     component="span"
     sx={{ backgroundColor: th.palette.warning.light, borderRadius: '2px' }}
    >
     {match}
    </Box>
    {end}
   </>
  );
 };

 const getRenderedValue = (item: GroupedFields) => {
  const rendered = displayValueByType(item.Value, item);
  if (
   typeof rendered === 'string' ||
   typeof rendered === 'number' ||
   typeof rendered === 'boolean'
  ) {
   return String(rendered);
  }
  if (
   typeof item.Value === 'string' ||
   typeof item.Value === 'number' ||
   typeof item.Value === 'boolean'
  ) {
   return String(item.Value);
  }
  return '';
 };

 const filteredGroupsMemo = useMemo(() => {
  if (!normalizedSearch) return groupsMemo;
  return Object.entries(groupsMemo).reduce((acc, [group, items]) => {
   const filteredItems = items.filter((item) => {
    const translation = normalizeSearch(item.Translation || '');
    const value = normalizeSearch(getRenderedValue(item));
    return (
     translation.includes(normalizedSearch) || value.includes(normalizedSearch)
    );
   });
   if (filteredItems.length > 0) {
    acc[group] = filteredItems;
   }
   return acc;
  }, {} as Record<string, GroupedFields[]>);
 }, [groupsMemo, normalizedSearch]);

 const filteredGroupKeys = Object.keys(filteredGroupsMemo);

 if (groupKeys.length === 0) {
  return (
   <>
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   </>
  );
 }

 return (
  <>
   <Box sx={{ mb: '20px' }}>
    <FieldSearch
     searchValue={searchValue}
     setSearchValue={setSearchValue}
     label={t('global.search')}
     bordered={true}
    />
   </Box>
   {filteredGroupKeys.length === 0 ? (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   ) : (
    filteredGroupKeys.map((group: string) => (
     <Box key={group} sx={{ '&:last-child': { mb: 0 }, mb: 3 }}>
      <Typography
       sx={{
        ...titleStyles,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'space-between',
       }}
      >
       {group === 'null' ? t('global.data') : group}{' '}
       <IconButton onClick={() => handleEditClick()} disabled={!apiRef}>
        <IconEdit style={{ color: th.palette.kemek.darkestGray }} />
       </IconButton>
      </Typography>

      <Box
       sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        border: `1px solid ${th.palette.kemek.lightestGray}`,
        borderRadius: 1,
        px: '12px',
        py: 1,
       }}
      >
       {group &&
        filteredGroupsMemo[group]?.map((item: GroupedFields) => {
         const hasSubArray =
          item.ForeignKeyUrl && item.ForeignKeyUrl.split('/').length > 4;
         const href =
          item.ForeignKeyUrl && item.Value !== '-'
           ? `${item.ForeignKeyUrl.split('/')[2]?.toLowerCase()}?id=${
              item.InnerValue
             }`
           : '';
         const renderedValue = displayValueByType(item.Value, item);
         const renderedValueText = getRenderedValue(item);

         return (
          <Box key={item.Key}>
           <Box
            sx={{
             display: 'flex',
             gap: 1,
             justifyContent: 'space-between',
            }}
           >
            <Typography
             component="span"
             sx={{
              fontWeight: 'medium',
              fontSize: 12,
             }}
            >
             {highlightMatch(item.Translation || '', normalizedSearch)}
            </Typography>
            {item.Type === 'model' &&
            item.Value !== null &&
            item.Value !== undefined &&
            item.Value !== '-' &&
            !hasSubArray ? (
             <Link
              href={href}
              target="_blank"
              sx={{ fontSize: 12, textAlign: 'right', wordBreak: 'break-word' }}
             >
              {normalizedSearch && renderedValueText
               ? highlightMatch(renderedValueText, normalizedSearch)
               : renderedValue}
             </Link>
            ) : (
             <Typography
              sx={{ fontSize: 12, textAlign: 'right', wordBreak: 'break-word' }}
             >
              {normalizedSearch && renderedValueText
               ? highlightMatch(renderedValueText, normalizedSearch)
               : renderedValue}
             </Typography>
            )}
           </Box>
          </Box>
         );
        })}
      </Box>
     </Box>
    ))
   )}
  </>
 );
};

export default InfoList;

